import { useState, useCallback, useMemo, useEffect } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type {
  Condition,
  Procedure,
  Bundle,
  Resource,
} from '@medplum/fhirtypes';
import { OperationOutcomeError } from '@medplum/core';
import { v4 as uuidv4 } from 'uuid';
import type {
  MedicalHistoryData,
  ActiveConditionLocal,
  PriorEventLocal,
  ProcedureLocal,
  ClinicalStatus,
} from '../types/medicalHistory';
import {
  CLINICAL_STATUS_CODES,
  VERIFICATION_STATUS,
  CONDITION_CATEGORY,
  PROCEDURE_STATUS,
} from '../types/medicalHistory';
import { onboardingService, type MedicalHistoryReviewed } from '../services/onboardingService';
import { parseFlexibleDate, getNoteText } from '../utils/fhirHelpers';

// Helper: Get clinical status from Condition
export function getClinicalStatus(condition: Condition): ClinicalStatus {
  const code = condition.clinicalStatus?.coding?.[0]?.code;
  if (code === 'resolved') return 'resolved';
  if (code === 'remission') return 'remission';
  return 'active';
}

// Extract MedicalHistoryData from FHIR resources
export function extractMedicalHistoryData(
  conditions: Condition[],
  procedures: Procedure[]
): MedicalHistoryData {
  const activeConditions: ActiveConditionLocal[] = [];
  const priorEvents: PriorEventLocal[] = [];

  for (const condition of conditions) {
    const status = getClinicalStatus(condition);
    const parsedDate = parseFlexibleDate(condition.onsetDateTime);
    const noteText = getNoteText(condition);

    if (status === 'active') {
      activeConditions.push({
        localId: uuidv4(),
        fhirId: condition.id,
        conditionName: condition.code?.text || '',
        onsetDate: parsedDate,
        comments: noteText || undefined,
      });
    } else {
      priorEvents.push({
        localId: uuidv4(),
        fhirId: condition.id,
        eventName: condition.code?.text || '',
        eventDate: parsedDate,
        status: status as 'resolved' | 'remission',
        comments: noteText || undefined,
      });
    }
  }

  const proceduresList: ProcedureLocal[] = procedures.map((proc) => {
    const noteText = getNoteText(proc);

    // Extract reason condition IDs (multiple references)
    const reasonConditionIds = proc.reasonReference
      ?.filter((ref) => ref.reference?.startsWith('Condition/'))
      .map((ref) => ref.reference!.replace('Condition/', ''));

    return {
      localId: uuidv4(),
      fhirId: proc.id,
      procedureName: proc.code?.text || '',
      procedureDate: parseFlexibleDate(proc.performedDateTime as string),
      reasonConditionIds: reasonConditionIds?.length ? reasonConditionIds : undefined,
      comments: noteText || undefined,
    };
  });

  return {
    activeConditions,
    priorEvents,
    procedures: proceduresList,
  };
}

// Build FHIR Condition from local data
export function buildConditionResource(
  local: ActiveConditionLocal | PriorEventLocal,
  patientId: string,
  type: 'active' | 'prior'
): Condition {
  const isActive = type === 'active';
  const status = isActive
    ? 'active'
    : (local as PriorEventLocal).status || 'resolved';

  const condition: Condition = {
    resourceType: 'Condition',
    ...(local.fhirId && { id: local.fhirId }),
    subject: { reference: `Patient/${patientId}` },
    code: {
      text: isActive
        ? (local as ActiveConditionLocal).conditionName
        : (local as PriorEventLocal).eventName,
    },
    clinicalStatus: {
      coding: [CLINICAL_STATUS_CODES[status]],
    },
    verificationStatus: {
      coding: [VERIFICATION_STATUS.unconfirmed],
    },
    category: [
      {
        coding: [CONDITION_CATEGORY.problemListItem],
      },
    ],
    // Only set recordedDate for new resources
    ...(!local.fhirId && { recordedDate: new Date().toISOString() }),
  };

  // Add onset date if provided
  const dateValue = isActive
    ? (local as ActiveConditionLocal).onsetDate?.value
    : (local as PriorEventLocal).eventDate?.value;

  if (dateValue) {
    condition.onsetDateTime = dateValue;
  }

  // Add comments as note
  if (local.comments) {
    condition.note = [{ text: local.comments }];
  }

  return condition;
}

// Build FHIR Procedure from local data
export function buildProcedureResource(
  local: ProcedureLocal,
  patientId: string
): Procedure {
  const procedure: Procedure = {
    resourceType: 'Procedure',
    ...(local.fhirId && { id: local.fhirId }),
    subject: { reference: `Patient/${patientId}` },
    status: PROCEDURE_STATUS,
    code: {
      text: local.procedureName,
    },
  };

  // Add performed date if provided
  if (local.procedureDate?.value) {
    procedure.performedDateTime = local.procedureDate.value;
  }

  // Add comments as note
  if (local.comments) {
    procedure.note = [{ text: local.comments }];
  }

  // Add reason references if linking to conditions (supports multiple)
  if (local.reasonConditionIds?.length) {
    procedure.reasonReference = local.reasonConditionIds.map((id) => ({
      reference: `Condition/${id}`,
    }));
  }

  return procedure;
}

export function useMedicalHistory() {
  const medplum = useMedplum();
  const baseProfile = useMedplumProfile() as Resource | undefined;

  const [conditions, setConditions] = useState<Condition[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewedFlags, setReviewedFlags] = useState<MedicalHistoryReviewed>(
    onboardingService.getMedicalHistoryReviewed()
  );

  const patientId = baseProfile?.id;
  const resourceType = (baseProfile as { resourceType?: string })?.resourceType;

  // Load existing data from Medplum
  const loadData = useCallback(async () => {
    if (!patientId || resourceType !== 'Patient') {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch conditions - don't fail if none found
      try {
        const conditionsResult = await medplum.search('Condition', {
          subject: `Patient/${patientId}`,
          _sort: '-onset-date',
        });
        const conditionsBundle = conditionsResult as Bundle<Condition>;
        setConditions(
          conditionsBundle.entry?.map((e) => e.resource as Condition) || []
        );
      } catch {
        // No conditions found - that's okay
        setConditions([]);
      }

      // Fetch procedures - don't fail if none found
      try {
        const proceduresResult = await medplum.search('Procedure', {
          subject: `Patient/${patientId}`,
          _sort: '-date',
        });
        const proceduresBundle = proceduresResult as Bundle<Procedure>;
        setProcedures(
          proceduresBundle.entry?.map((e) => e.resource as Procedure) || []
        );
      } catch {
        // No procedures found - that's okay
        setProcedures([]);
      }
    } catch (err) {
      console.error('Failed to load medical history:', err);
      if (err instanceof OperationOutcomeError) {
        console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
      }
      setError('Unable to load medical history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [medplum, patientId, resourceType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Extract to local format
  const medicalHistoryData = useMemo(
    () => extractMedicalHistoryData(conditions, procedures),
    [conditions, procedures]
  );

  // Available conditions for procedure linking (includes both active and prior)
  const availableConditions = useMemo(() => {
    return conditions.map((c) => ({
      id: c.id!,
      name: c.code?.text || 'Unknown condition',
    }));
  }, [conditions]);

  // Data status for each section
  const hasActiveConditions = medicalHistoryData.activeConditions.length > 0;
  const hasPriorEvents = medicalHistoryData.priorEvents.length > 0;
  const hasProcedures = medicalHistoryData.procedures.length > 0;

  // Section is complete if: has data OR marked as "no content"
  const isActiveConditionsComplete = hasActiveConditions || reviewedFlags.noActiveConditions;
  const isPriorEventsComplete = hasPriorEvents || reviewedFlags.noPriorEvents;
  const isProceduresComplete = hasProcedures || reviewedFlags.noProcedures;

  // Overall completion: all sections complete
  const isComplete = isActiveConditionsComplete && isPriorEventsComplete && isProceduresComplete;

  // Has any data at all
  const hasAnyData = hasActiveConditions || hasPriorEvents || hasProcedures;

  // Update reviewed flags
  const setReviewedFlag = useCallback((flag: keyof MedicalHistoryReviewed, value: boolean) => {
    const updated = onboardingService.setMedicalHistoryReviewed({ [flag]: value });
    setReviewedFlags(updated);
  }, []);

  // Save all changes using two-phase approach
  const saveAll = useCallback(
    async (data: MedicalHistoryData): Promise<boolean> => {
      if (!patientId) {
        setError('Patient data not loaded');
        return false;
      }

      // Validate required fields
      for (const condition of data.activeConditions) {
        if (!condition.isDeleted && !condition.conditionName.trim()) {
          setError('Please fill in the condition name for all entries.');
          return false;
        }
      }
      for (const event of data.priorEvents) {
        if (!event.isDeleted && !event.eventName.trim()) {
          setError('Please fill in the event name for all entries.');
          return false;
        }
      }
      for (const procedure of data.procedures) {
        if (!procedure.isDeleted && !procedure.procedureName.trim()) {
          setError('Please fill in the procedure name for all entries.');
          return false;
        }
      }

      try {
        setIsSaving(true);
        setError(null);

        // Phase 1: Save all conditions first (so procedures can reference them)
        const conditionOperations: Promise<Condition>[] = [];

        // Active conditions
        for (const condition of data.activeConditions) {
          if (condition.isDeleted && condition.fhirId) {
            await medplum.deleteResource('Condition', condition.fhirId);
          } else if (!condition.isDeleted && condition.conditionName) {
            const resource = buildConditionResource(
              condition,
              patientId,
              'active'
            );
            if (condition.fhirId) {
              conditionOperations.push(medplum.updateResource(resource));
            } else {
              conditionOperations.push(medplum.createResource(resource));
            }
          }
        }

        // Prior events
        for (const event of data.priorEvents) {
          if (event.isDeleted && event.fhirId) {
            await medplum.deleteResource('Condition', event.fhirId);
          } else if (!event.isDeleted && event.eventName) {
            const resource = buildConditionResource(event, patientId, 'prior');
            if (event.fhirId) {
              conditionOperations.push(medplum.updateResource(resource));
            } else {
              conditionOperations.push(medplum.createResource(resource));
            }
          }
        }

        // Wait for all conditions to be saved
        await Promise.all(conditionOperations);

        // Phase 2: Save procedures (can now reference saved conditions)
        const procedureOperations: Promise<Procedure>[] = [];

        for (const procedure of data.procedures) {
          if (procedure.isDeleted && procedure.fhirId) {
            await medplum.deleteResource('Procedure', procedure.fhirId);
          } else if (!procedure.isDeleted && procedure.procedureName) {
            const resource = buildProcedureResource(procedure, patientId);
            if (procedure.fhirId) {
              procedureOperations.push(medplum.updateResource(resource));
            } else {
              procedureOperations.push(medplum.createResource(resource));
            }
          }
        }

        await Promise.all(procedureOperations);

        // Reload data to get updated IDs
        await loadData();
        return true;
      } catch (err) {
        console.error('Failed to save medical history:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to save. Please try again.');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [medplum, patientId, loadData]
  );

  return {
    medicalHistoryData,
    isLoading,
    isSaving,
    error,
    isComplete,
    hasAnyData,
    // Per-section status
    hasActiveConditions,
    hasPriorEvents,
    hasProcedures,
    isActiveConditionsComplete,
    isPriorEventsComplete,
    isProceduresComplete,
    // Reviewed flags
    reviewedFlags,
    setReviewedFlag,
    // Actions
    saveAll,
    refresh: loadData,
    availableConditions,
  };
}
